import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema, TraceIdSchema, CorrelationIdSchema } from "../primitives/ids.js";
/** notification-event.v1.0 — tenant-scoped notification/email-event contracts (PF-007). */
export const NotificationChannelSchema = z.enum(["EMAIL", "IN_APP"]);
export const NotificationTemplateStatusSchema = z.enum(["ACTIVE", "RETIRED"]);
/**
 * Distinct delivery states — never invent SENT.
 * Queue unavailable, provider unavailable, retry scheduled, delivery failed,
 * and in-app pending remain distinct.
 */
export const NotificationDeliveryStatusSchema = z.enum([
    "QUEUED",
    "QUEUE_UNAVAILABLE",
    "RETRY_SCHEDULED",
    "PROVIDER_UNAVAILABLE",
    "DELIVERY_FAILED",
    "MAX_RETRIES_EXCEEDED",
    "INVALID_TEMPLATE",
    "REJECTED",
    "ACCEPTED_LOCAL_STUB",
    "IN_APP_PENDING",
    "IN_APP_UNREAD",
    "IN_APP_READ",
]);
export const NotificationTemplateIdSchema = z
    .string()
    .regex(/^ntpl_[a-zA-Z0-9_-]+$/)
    .brand();
export const NotificationDeliveryEventIdSchema = z
    .string()
    .regex(/^ndev_[a-zA-Z0-9_-]+$/)
    .brand();
export const InAppNotificationIdSchema = z
    .string()
    .regex(/^ninapp_[a-zA-Z0-9_-]+$/)
    .brand();
export const NotificationTemplateKeySchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z][a-z0-9_.-]*$/);
export const NotificationTemplateV1Schema = z
    .object({
    schemaVersion: z.literal("notification-template.v1.0"),
    templateId: NotificationTemplateIdSchema,
    agencyId: AgencyIdSchema,
    templateKey: NotificationTemplateKeySchema,
    version: z.number().int().positive(),
    channel: NotificationChannelSchema,
    subjectTemplate: z.string().min(1).max(200),
    bodyTemplate: z.string().min(1).max(8000),
    status: NotificationTemplateStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const NotificationTemplateCreateRequestV1Schema = z
    .object({
    schemaVersion: z.literal("notification-template-create.v1.0"),
    templateKey: NotificationTemplateKeySchema,
    channel: NotificationChannelSchema.optional(),
    subjectTemplate: z.string().min(1).max(200),
    bodyTemplate: z.string().min(1).max(8000),
})
    .strict();
export const NotificationTemplateListV1Schema = z
    .object({
    schemaVersion: z.literal("notification-template-list.v1.0"),
    items: z.array(NotificationTemplateV1Schema),
})
    .strict();
export const NotificationSendRequestV1Schema = z
    .object({
    schemaVersion: z.literal("notification-send.v1.0"),
    templateKey: NotificationTemplateKeySchema,
    templateVersion: z.number().int().positive().optional(),
    channel: NotificationChannelSchema.optional(),
    recipientRef: z.string().min(1).max(320),
    recipientUserId: z.string().min(1).max(120).optional(),
    projectId: ProjectIdSchema.optional(),
    variables: z.record(z.string(), z.string().max(500)).optional(),
    idempotencyKey: z.string().min(8).max(200).optional(),
    traceId: TraceIdSchema.optional(),
    correlationId: CorrelationIdSchema.optional(),
})
    .strict();
export const NotificationDeliveryEventV1Schema = z
    .object({
    schemaVersion: z.literal("notification-delivery-event.v1.0"),
    deliveryEventId: NotificationDeliveryEventIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema.optional(),
    templateKey: NotificationTemplateKeySchema,
    // 0 allowed only for failed lookup ledger rows (template not found).
    templateVersion: z.number().int().min(0),
    channel: NotificationChannelSchema,
    recipientRef: z.string().min(1).max(320),
    recipientUserId: z.string().min(1).max(120).optional(),
    status: NotificationDeliveryStatusSchema,
    failureReason: z.string().max(500).optional(),
    provider: z.string().min(1).max(80),
    payloadSummary: z.string().min(1).max(4000),
    idempotencyKey: z.string().min(8).max(200).optional(),
    attemptCount: z.number().int().min(0).default(0),
    maxAttempts: z.number().int().min(1).max(20).default(5),
    nextRetryAt: z.string().datetime().optional(),
    lastAttemptAt: z.string().datetime().optional(),
    traceId: TraceIdSchema,
    correlationId: CorrelationIdSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const NotificationSendResultV1Schema = z
    .object({
    schemaVersion: z.literal("notification-send-result.v1.0"),
    ok: z.boolean(),
    deliveryEvent: NotificationDeliveryEventV1Schema,
})
    .strict();
export const NotificationDeliveryEventListV1Schema = z
    .object({
    schemaVersion: z.literal("notification-delivery-event-list.v1.0"),
    items: z.array(NotificationDeliveryEventV1Schema),
})
    .strict();
export const InAppNotificationStatusSchema = z.enum(["UNREAD", "READ"]);
export const InAppNotificationV1Schema = z
    .object({
    schemaVersion: z.literal("in-app-notification.v1.0"),
    inAppNotificationId: InAppNotificationIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema.optional(),
    userId: z.string().min(1).max(120),
    deliveryEventId: NotificationDeliveryEventIdSchema,
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(8000),
    status: InAppNotificationStatusSchema,
    readAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const InAppNotificationListV1Schema = z
    .object({
    schemaVersion: z.literal("in-app-notification-list.v1.0"),
    items: z.array(InAppNotificationV1Schema),
})
    .strict();
export const InAppNotificationAckRequestV1Schema = z
    .object({
    schemaVersion: z.literal("in-app-notification-ack.v1.0"),
    inAppNotificationId: InAppNotificationIdSchema,
})
    .strict();
export const InAppNotificationAckResultV1Schema = z
    .object({
    schemaVersion: z.literal("in-app-notification-ack-result.v1.0"),
    ok: z.boolean(),
    notification: InAppNotificationV1Schema.optional(),
    error: z
        .enum(["not_found", "tenant_mismatch", "permission_denied", "audit_append_failed"])
        .optional(),
})
    .strict();
//# sourceMappingURL=notification-event.js.map